import { type FormEvent, useState } from 'react';
import {
  AdminApiError,
  type AdminUser,
  createInvitation,
  deactivateUser,
  deleteUser,
  type Invitation,
  reactivateUser,
  resendInvitation,
  revokeInvitation,
  useAdminUsers,
  useInvitations,
} from '@/lib/admin';
import { useSession } from '@/lib/authClient';

/** Light admin panel: invite users, manage pending invites, manage accounts. The
 *  server (`requireAdmin`) is the real boundary; this page assumes admin (the
 *  router only renders it for admins). */
export const AdminPage = () => {
  const invitations = useInvitations();
  const users = useAdminUsers();
  const { data } = useSession();

  return (
    <div className="min-h-dvh bg-gray-50 p-6 text-gray-800">
      <div className="mx-auto flex max-w-3xl flex-col gap-8">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Admin</h1>
          <a href="/" className="text-sm text-purple-600 hover:underline">
            ← Back to app
          </a>
        </header>
        <InviteSection onInvited={invitations.refresh} />
        <PendingInvites list={invitations.items} onChange={invitations.refresh} />
        <Users list={users.items} currentUserId={data?.user?.id} onChange={users.refresh} />
      </div>
    </div>
  );
};

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-2xl bg-white p-5 shadow-sm">
    <h2 className="mb-4 text-lg font-semibold">{title}</h2>
    {children}
  </section>
);

const InviteSection = ({ onInvited }: { onInvited: () => void }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      await createInvitation(email);
      setMessage({ ok: true, text: `Invitation sent to ${email}.` });
      setEmail('');
      onInvited();
    } catch (error) {
      const code = error instanceof AdminApiError ? error.code : undefined;
      setMessage({
        ok: false,
        text:
          code === 'USER_EXISTS'
            ? 'That email already has an account.'
            : code === 'ALREADY_INVITED'
              ? 'That email already has a pending invite.'
              : 'Could not send the invitation.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="Invite a user">
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col gap-1 text-sm font-medium">
          Email
          <input
            type="email"
            required={true}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="rounded-xl border border-gray-300 px-3 py-2 outline-none focus:border-purple-500"
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-purple-600 px-4 py-2 font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
        >
          {submitting ? 'Sending…' : 'Send invite'}
        </button>
      </form>
      {message ? (
        <p className={`mt-3 text-sm ${message.ok ? 'text-green-600' : 'text-red-600'}`}>
          {message.text}
        </p>
      ) : null}
    </Card>
  );
};

const statusStyles: Record<Invitation['status'], string> = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-green-100 text-green-700',
  revoked: 'bg-gray-200 text-gray-600',
  expired: 'bg-red-100 text-red-700',
};

const PendingInvites = ({ list, onChange }: { list: Array<Invitation>; onChange: () => void }) => {
  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    await fn().catch(() => {});
    onChange();
  };

  return (
    <Card title="Invitations">
      {list.length === 0 ? (
        <p className="text-sm text-gray-500">No invitations yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100">
          {list.map((invite) => (
            <li key={invite.id} className="flex items-center gap-3 py-3">
              <span className="flex-1 truncate text-sm">{invite.email}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[invite.status]}`}
              >
                {invite.status}
              </span>
              {invite.status === 'pending' || invite.status === 'expired' ? (
                <>
                  <button
                    type="button"
                    onClick={() => act(() => resendInvitation(invite.id))}
                    className="text-xs text-purple-600 hover:underline"
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Revoke the invite for ${invite.email}?`)) {
                        void act(() => revokeInvitation(invite.id));
                      }
                    }}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Revoke
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const Users = ({
  list,
  currentUserId,
  onChange,
}: {
  list: Array<AdminUser>;
  currentUserId?: string;
  onChange: () => void;
}) => {
  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    await fn().catch(() => {});
    onChange();
  };

  return (
    <Card title="Users">
      {list.length === 0 ? (
        <p className="text-sm text-gray-500">No users.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100">
          {list.map((user) => {
            const isSelf = user.id === currentUserId;
            return (
              <li key={user.id} className="flex items-center gap-3 py-3">
                <span className="flex-1 truncate text-sm">
                  {user.name}
                  <span className="ml-2 text-gray-400">{user.email}</span>
                  {user.role === 'admin' ? (
                    <span className="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                      admin
                    </span>
                  ) : null}
                </span>
                {user.active ? null : (
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                    deactivated
                  </span>
                )}
                {isSelf ? (
                  <span className="text-xs text-gray-400">you</span>
                ) : (
                  <>
                    {user.active ? (
                      <button
                        type="button"
                        onClick={() => act(() => deactivateUser(user.id))}
                        className="text-xs text-amber-600 hover:underline"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => act(() => reactivateUser(user.id))}
                        className="text-xs text-green-600 hover:underline"
                      >
                        Reactivate
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Permanently delete ${user.email}?`)) {
                          void act(() => deleteUser(user.id));
                        }
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};
